import { Icon } from '#app/components/ui/icon.tsx'
import { cn } from '#app/utils/misc.tsx'

export interface FeaturesGridProps {
	title?: string
	subtitle?: string
	columns?: number
	background?: 'default' | 'muted' | 'primary-light'
	items: {
		title: string
		description: string
		icon?: string
	}[]
}

export function FeaturesGrid({
	title,
	subtitle,
	items,
	columns = 4,
	background = 'default',
}: FeaturesGridProps) {
	const bgClasses = {
		default: 'bg-background',
		muted: 'bg-muted/30',
		'primary-light': 'bg-primary/5',
	}

	const gridCols = {
		2: 'grid-cols-1 sm:grid-cols-2',
		3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
		4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
	}

	return (
		<section className={cn('py-16 md:py-24', bgClasses[background])}>
			<div className="container mx-auto px-4">
				{(title || subtitle) && (
					<div className="mx-auto mb-14 flex max-w-2xl flex-col gap-3 text-center">
						{title && <h2 className="text-title-2 text-foreground">{title}</h2>}
						{subtitle && (
							<p className="text-muted-foreground text-sm leading-relaxed md:text-base">
								{subtitle}
							</p>
						)}
					</div>
				)}

				<div
					className={cn(
						'mx-auto grid max-w-6xl gap-8',
						gridCols[columns as keyof typeof gridCols] || gridCols[4],
					)}
				>
					{items.map((item, index) => (
						<FeatureCard key={index} item={item} />
					))}
				</div>
			</div>
		</section>
	)
}

function FeatureCard({ item }: { item: FeaturesGridProps['items'][number] }) {
	return (
		<div className="bg-background relative flex flex-col justify-between gap-6 px-6 pt-8 pb-6 shadow-xs">
			{/* Extended borders */}
			<div className="bg-border absolute -inset-y-4 -left-px w-px" />
			<div className="bg-border absolute -inset-y-4 -right-px w-px" />
			<div className="bg-border absolute -inset-x-4 -top-px h-px" />
			<div className="bg-border absolute -right-4 -bottom-px -left-4 h-px" />

			{/* Corner decorator */}
			<div className="border-border bg-background absolute -top-1 -left-1 size-2 rounded-full border" />

			{/* Icon */}
			{item.icon && (
				<div className="border-border bg-muted relative z-10 flex w-fit items-center justify-center rounded-lg border p-3">
					<Icon
						name={item.icon as any}
						className="text-secondary-foreground size-5"
					/>
				</div>
			)}

			{/* Text */}
			<div className="relative z-10 space-y-2">
				<h3 className="text-foreground text-base font-medium">{item.title}</h3>
				<p className="text-muted-foreground text-xs leading-relaxed">
					{item.description}
				</p>
			</div>
		</div>
	)
}
